import React from "react"
import {get} from "lodash"
import {Auth} from "types/auth"
import {AdvisorTitle, StudentInfo, StaffInfo, OtherInfo, TotalProgress, ProgressItem, StudentScore} from "uiKit"
import {
    getTotalHourStatistics,
    getStudentTotalProgress,
    checkPermission,
    isFieldActive,
    getFieldLabel,
    formatDate
} from "helpers"
import {useModel} from "hooks"
import styles from "./UsersTab.module.css"
import cx from "classnames"
import {Permissions} from "@edular/permissions"
import {Settings} from "types/settings"
import {Col, Row} from "antd"

type Props = {
    user: Auth.UserProfile
    onLoadStudentStats?: (stats: Auth.StudentStatistics) => void
}

export function ActiveUser(props: Props) {
    const {user} = props
    const model = useModel()

    const showTotalHours = isFieldActive(model, Settings.AcademicLabel.TotalHours)
    const showTotalMajorHours = isFieldActive(model, Settings.AcademicLabel.TotalMajorHours)
    const totalHoursLabel = getFieldLabel(model, Settings.AcademicLabel.TotalHours)
    const totalMajorHoursLabel = getFieldLabel(model, Settings.AcademicLabel.TotalMajorHours)
    const canShowLastSignIn = model.isStaffOrAdmin()

    const hasPermissionsToSeeAcademicProgress = checkPermission(
        {
            staff: [Permissions.Staff.Users.Students.AcademicProgress.View]
        },
        model
    )

    const renderLeftInfo = (studentStats: Auth.StudentStatistics, activeProfileId: number) => {
        const activeProfile = user.profiles.find((profile) => profile.id === activeProfileId)
        const programVersionTotalClockHours = get(activeProfile, "currentMajorVersionId.totalClockHours", 0)
        const totalHours = getTotalHourStatistics(model, studentStats, programVersionTotalClockHours)

        return (
            <div className={styles.studentLeftInfo}>
                <TotalProgress percent={getStudentTotalProgress(studentStats)} />
                {hasPermissionsToSeeAcademicProgress && (
                    <ProgressItem
                        title="Total hours"
                        totalTitle={totalHoursLabel}
                        totalHours={totalHours}
                        showTotalHours={showTotalHours}
                        totalContractedHours={studentStats.totalHours}
                        totalScheduleHoursToLda={studentStats.totalScheduleHoursToLda}
                    />
                )}
            </div>
        )
    }

    const renderLastSignIn = () => {
        const datetimeFormat = model.getUserDateTimeFormat()

        return (
            <div className={cx(styles.studentLeftInfo, styles.studentRightInfo__margin)}>
                <Row gutter={[12, 12]}>
                    <Col span={24}>
                        <div className={styles.rightInfoItem}>
                            <span className={styles.rightInfoItem__title}>Last sign-in (mobile)</span>
                            <span className={styles.rightInfoItem__value}>
                                {user.mobileLastOpenedAt ? formatDate(user.mobileLastOpenedAt, datetimeFormat) : "-"}
                            </span>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div className={styles.rightInfoItem}>
                            <span className={styles.rightInfoItem__title}>Last sign-in (web)</span>
                            <span className={styles.rightInfoItem__value}>
                                {user.webLastOpenedAt ? formatDate(user.webLastOpenedAt, datetimeFormat) : "-"}
                            </span>
                        </div>
                    </Col>
                </Row>
            </div>
        )
    }

    const renderRightInfo = (studentStats: Auth.StudentStatistics) => {
        if (!hasPermissionsToSeeAcademicProgress && !canShowLastSignIn) {
            return null
        }

        return (
            <div className={styles.studentRightInfo}>
                {hasPermissionsToSeeAcademicProgress && (
                    <div className={cx(styles.studentLeftInfo, styles.studentRightInfo__margin)}>
                        <StudentScore studentStats={studentStats} />
                    </div>
                )}
                {canShowLastSignIn && renderLastSignIn()}
            </div>
        )
    }

    if (user.type === Auth.UserProfileType.Student) {
        return (
            <StudentInfo
                student={user}
                isDetailButtonVisible={false}
                advisorTitle={AdvisorTitle.advisors}
                renderExtraPhoneEmail={({studentStats, activeProfileId}) =>
                    renderLeftInfo(studentStats, activeProfileId)
                }
                renderRightInfo={({studentStats}) => renderRightInfo(studentStats)}
                onLoadStudentStats={props.onLoadStudentStats}
            />
        )
    }

    if (user.type === Auth.UserProfileType.Staff) {
        return (
            <StaffInfo
                user={user}
                renderRightInfo={() =>
                    canShowLastSignIn ? <div className={styles.studentRightInfo}>{renderLastSignIn()}</div> : null
                }
            />
        )
    }

    if (user.type === Auth.UserProfileType.Others) {
        return (
            <OtherInfo
                user={user}
                renderRightInfo={() =>
                    canShowLastSignIn ? <div className={styles.studentRightInfo}>{renderLastSignIn()}</div> : null
                }
            />
        )
    }

    return null
}
